import { useDeviceType } from '@cian/ui-kit';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setBannerBlockHeight } from './helpers';
import { useGetCurrentBanner } from './utils';
import { getTermsValue } from '../../../common/packages/JsonQuery';
import { changeForceClosedBanner } from '../../actions/promoInventory';
import { BANNER_ID, EBanners } from '../../constants/banners';
import { selectIsForceClosedBanner } from '../../selectors/promoInventory';
import { selectJsonQuery } from '../../selectors/results';
import { AdfoxSpecialProjectContainer } from '../AdfoxSpecialProjectContainer';
import { CPDBannerContainer } from '../CPDBannerContainer';
import { FskBannerContainer } from '../FskBannerContainer';
import { LeadsContainer } from '../LeadsContainer';
import { ProPlusBannerContainer } from '../ProPlusBannerContainer';
import { RealtorReportBannerContainer } from '../RealtorReportBannerContainer';

/**
 * Здесь предполагаю, что будет логика для баннеров для пакетов
 * https://conf.cian.tech/pages/viewpage.action?pageId=1299053455
 */
/**
 * Добавление новых баннеров:
 *  - добавляем в энаму (EBanners) баннеров новый баннер src/shared/constants/banners.ts
 *  - добавляем в константу мобильных размеров баннера (bannersMobileHeight) новый баннер src/shared/constants/banners.ts
 *  - добавляем в хук useGetCurrentBanner условия рендера нового баннера
 *  - добавляем условия установки высоты для мобильной версии баннера src/node/services/getBanner/getBanner.tsx
 */
export function BannersContainer() {
  const [open, setOpen] = React.useState(false);
  const currentBanner = useGetCurrentBanner();
  const isForceClosedBanner = useSelector(selectIsForceClosedBanner);
  const jsonQuery = useSelector(selectJsonQuery);
  const dispatch = useDispatch();

  const deviceType = useDeviceType();

  /**
   * Флаг «пользователь закрыл баннер» (isForceClosedBanner) выставлялся навсегда
   * на весь маунт и нигде не сбрасывался (единственный диспатч — из handleClose,
   * всегда `true`). Из-за этого once закрытый баннер не появлялся снова даже после
   * смены региона: перевзвод `open` ниже гейтится `!isForceClosedBanner`, и open
   * оставался false (баг CD-279468/CD-279893 — возврат в МиМО для СТОУН не показывал
   * баннер). Смена региона — это новый контекст показа, поэтому сбрасываем флаг.
   * Пан/зум регион не меняют → regionKey стабилен → баннер, закрытый в текущем
   * регионе, остаётся закрытым (клик по пину/шторка выдачи регион тоже не меняют).
   */
  const regionKey = (getTermsValue('region')(jsonQuery) ?? []).join('-');
  const isInitialRegionRef = React.useRef(true);
  React.useEffect(() => {
    if (isInitialRegionRef.current) {
      isInitialRegionRef.current = false;

      return;
    }

    dispatch(changeForceClosedBanner(false));
  }, [regionKey, dispatch]);

  const handleClose = React.useCallback(() => {
    setOpen(false);
    dispatch(changeForceClosedBanner(true));

    if (deviceType !== 'desktop') {
      // Выключил проверку наличия контейнера
      /* istanbul ignore next */
      document.getElementById(BANNER_ID)?.remove();
    }
  }, [deviceType, dispatch]);

  /**
   * Баннер может стать доступным после маунта (например, при смене региона
   * на МиМО для СТОУН, CD-279468), поэтому перевзводим open при появлении
   * баннера. Закрытый пользователем баннер не переоткрываем (isForceClosedBanner).
   */
  React.useEffect(() => {
    if (currentBanner && !isForceClosedBanner) {
      setOpen(true);
    }
  }, [currentBanner, isForceClosedBanner]);

  React.useEffect(() => {
    const container = document.getElementById(BANNER_ID);

    setBannerBlockHeight(container, deviceType, currentBanner);
  }, [currentBanner, deviceType]);

  switch (currentBanner) {
    case EBanners.Fsk:
      return <FskBannerContainer open={open} onClose={handleClose} />;

    case EBanners.Adfox:
      return <AdfoxSpecialProjectContainer open={open} onClose={handleClose} />;

    case EBanners.CPD:
      return <CPDBannerContainer open={open} onClose={handleClose} />;

    case EBanners.Leads:
      return <LeadsContainer open={open} onClose={handleClose} />;

    case EBanners.ProPlus:
      return <ProPlusBannerContainer open={open} onClose={handleClose} />;

    case EBanners.RealtorReport:
      return <RealtorReportBannerContainer open={open} onClose={handleClose} />;
  }

  return null;
}
